import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { MoneyTypeEnum } from 'src/enums';
import { MoneyEvent } from 'src/typings';
import { MoneyEventService } from './money-event.service';

export type MoneyDayRefundByType = Record<MoneyTypeEnum, number>;

export interface MoneyDayRefundReportRow {
  event_id: number;
  occurred_at: Date | string;
  money_day_id?: number | null;
  money_type?: MoneyTypeEnum | null;
  amount: number;
  refund_id?: number | null;
  account_operation_id?:number|null;
  client_id?:number|null;
  org_id?:number|null;
  invoice_id?: number | null;
  order_id?: number | null;
  return_to_warehouse_id?: number | null;
  cash_out_doc_id?: number | null;
  execution_channel?: string | null;
  manager_user_name?: string | null;
  warehouse_name?: string | null;
}

export interface MoneyDayRefundReport {
  money_day_id: number | null;
  total: number;
  by_type: MoneyDayRefundByType;
  rows: MoneyDayRefundReportRow[];
  total_events: number;
  has_more: boolean;
}

const REPORT_LIMIT = 500;

@Injectable({
  providedIn: 'root'
})
export class MoneyDayRefundReportService {
  constructor(private readonly moneyEventService: MoneyEventService) {}

  getReport(moneyDayId?: number | null): Observable<MoneyDayRefundReport> {
    const normalizedMoneyDayId = Number(moneyDayId || 0);
    if (!normalizedMoneyDayId) {
      return of(this.createEmptyReport(null));
    }

    return this.moneyEventService
      .search({
        money_day_id: normalizedMoneyDayId,
        operation_group:'REFUND',
        limit: REPORT_LIMIT,
        offset: 0
      })
      .pipe(
        map((response) =>
          this.buildReport(normalizedMoneyDayId, response.items || [], Number(response.total || 0))
        )
      );
  }

  createEmptyReport(moneyDayId: number | null = null): MoneyDayRefundReport {
    return {
      money_day_id: moneyDayId,
      total: 0,
      by_type: this.createEmptyByType(),
      rows: [],
      total_events: 0,
      has_more: false
    };
  }

  mergeReports(reports: Array<MoneyDayRefundReport | null | undefined>): MoneyDayRefundReport {
    const merged = this.createEmptyReport(null);
    for (const report of reports || []) {
      if (!report) continue;
      merged.total = this.roundMoney(merged.total + Number(report.total || 0));
      merged.total_events += Number(report.total_events || report.rows?.length || 0);
      merged.has_more = merged.has_more || Boolean(report.has_more);
      for (const moneyType of Object.values(MoneyTypeEnum)) {
        merged.by_type[moneyType] = this.roundMoney(
          Number(merged.by_type[moneyType] || 0) + Number(report.by_type?.[moneyType] || 0)
        );
      }
      merged.rows.push(...(report.rows || []));
    }

    merged.rows.sort((left, right) => {
      const leftTime = new Date(left.occurred_at).getTime() || 0;
      const rightTime = new Date(right.occurred_at).getTime() || 0;
      if (rightTime !== leftTime) {
        return rightTime - leftTime;
      }
      return Number(right.event_id || 0) - Number(left.event_id || 0);
    });

    return merged;
  }

  private buildReport(
    moneyDayId: number,
    events: MoneyEvent[],
    totalEvents: number
  ): MoneyDayRefundReport {
    const report = this.createEmptyReport(moneyDayId);
    report.total_events = totalEvents;
    report.has_more = totalEvents > REPORT_LIMIT;

    report.rows = (events || []).map((event) => this.toReportRow(event));
    for (const row of report.rows) {
      report.total = this.roundMoney(report.total + row.amount);
      if (row.money_type && row.money_type in report.by_type) {
        report.by_type[row.money_type] = this.roundMoney(
          Number(report.by_type[row.money_type] || 0) + row.amount
        );
      }
    }

    return report;
  }

  private toReportRow(event: MoneyEvent): MoneyDayRefundReportRow {
    const payload = event.payload_json || {};

    return {
      event_id: Number(event.id || 0),
      occurred_at: event.occurred_at,
      money_day_id: event.money_day_id || null,
      money_type: event.money_type || null,
      amount: this.roundMoney(Math.abs(Number(event.amount_signed || 0))),
      refund_id: event.source_doc_type==='customer_account_operation'?null:this.readPayloadNumber(payload, 'refund_id') || event.source_doc_id || null,
      account_operation_id:event.source_doc_type==='customer_account_operation'?event.source_doc_id:null,
      client_id:this.readPayloadNumber(payload,'client_id'),org_id:this.readPayloadNumber(payload,'org_id'),
      invoice_id: this.readPayloadNumber(payload, 'invoice_id'),
      order_id: this.readPayloadNumber(payload, 'order_id'),
      return_to_warehouse_id: this.readPayloadNumber(payload, 'return_to_warehouse_id'),
      cash_out_doc_id: this.readPayloadNumber(payload, 'cash_out_doc_id'),
      execution_channel: this.readPayloadString(payload, 'execution_channel'),
      manager_user_name: event.manager_user_name || null,
      warehouse_name: event.warehouse_name || null
    };
  }

  private createEmptyByType(): MoneyDayRefundByType {
    return {
      [MoneyTypeEnum.nal]: 0,
      [MoneyTypeEnum.terminal]: 0,
      [MoneyTypeEnum.card]: 0,
      [MoneyTypeEnum.bn]: 0,
      [MoneyTypeEnum.A]: 0
    };
  }

  private readPayloadNumber(payload: Record<string, unknown>, key: string): number | null {
    const value = Number(payload?.[key] || 0);
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  private readPayloadString(payload: Record<string, unknown>, key: string): string | null {
    const value = String(payload?.[key] || '').trim();
    return value || null;
  }

  private roundMoney(value: number) {
    return Math.round(Number(value || 0) * 100) / 100;
  }
}
